import { useMutation } from '@apollo/client';
import {
  Select as AntdSelect,
  Button,
  Form,
  Input,
  Space,
  Typography
} from 'antd';
import React, { useEffect, useMemo } from 'react';
import {
  AUTO_ADVANCE_DEFAULTS,
  AUTO_GENERATED_SORT_BY_TYPES,
  MODULE_TYPES,
  PAGE_TYPES,
  RATIOS,
  STATIC_DATA_KEYS,
  STATUS_TYPES
} from '../../../../../common/constants';
import { formValidatorRules } from '../../../../../common/utils';
import useStaticData from '../../../../../hooks/useStaticData';
import { FORM_TYPES } from '../../../context/EditPageProvider';
import {
  CREATE_PAGE_MODULE,
  UPDATE_PAGE_MODULE
} from '../../../graphql/Mutations';
import {
  ModuleFields,
  Permissions,
  RedirectConfigInput,
  SelectCollections,
  ShowFields,
  Switch
} from './FormInputs';

const getInitialValues = (type) => ({
  title: '',
  description: '',
  status: STATUS_TYPES.PUBLISHED,
  isDefaultModule: false,
  permissions: [],
  settings: {
    title: true,
    description: true,
    viewAll: true,
    numberOfItems: true,
    separator: true,
    collectionDescription: true,
    allText: true,
    collectionTitle: true,
    collectionImage: true,
    showImageThumbnail: true,
    showProgressBar: true,
    navigationArrow: true,
    autoAdvanceEnabled:
      type === MODULE_TYPES.FEATURED_COLLECTION_SLIDER ? false : undefined
  },
  config: {
    ...(type !== MODULE_TYPES.COLLECTION_DETAIL && {
      autoGenerate: false
    }),
    ...(type !== MODULE_TYPES.COLLECTION_PILL_CAROUSEL && {
      aspectRatio: RATIOS.SMALL
    }),
    autoGenerateSortBy: null,
    autoGenerateLimit: null,
    collections:
      type === MODULE_TYPES.FEATURED_COLLECTION ||
      type === MODULE_TYPES.COLLECTION_DETAIL
        ? { id: '', url: '', title: '' }
        : [],
    ...(type === MODULE_TYPES.COLLECTION_DETAIL && {
      collectionId: null
    }),
    ...([
      MODULE_TYPES.COLLECTION_PILL_CAROUSEL,
      MODULE_TYPES.COLLECTION_CAROUSEL
    ]?.includes(type) && {
      redirectConfig: {
        internalPageType: null,
        slug: null,
        url: null
      }
    }),
    ...(type === MODULE_TYPES.FEATURED_COLLECTION_SLIDER && {
      autoAdvanceInterval: AUTO_ADVANCE_DEFAULTS.INTERVAL,
      autoAdvanceResumeDelay: AUTO_ADVANCE_DEFAULTS.RESUME_DELAY
    })
  }
});

const RATIOS_OPTIONS = [
  {
    label: 'Small',
    value: RATIOS.SMALL
  },
  {
    label: 'Medium',
    value: RATIOS.MEDIUM
  },
  {
    label: 'Large',
    value: RATIOS.LARGE
  }
];

const SETTINGS = [
  {
    name: 'title',
    label: 'Title',
    allowedTypes: [
      MODULE_TYPES.COLLECTION_GRID,
      MODULE_TYPES.COLLECTION_LIST,
      MODULE_TYPES.COLLECTION_CAROUSEL,
      MODULE_TYPES.FEATURED_COLLECTION,
      MODULE_TYPES.COLLECTION_PILL_CAROUSEL,
      MODULE_TYPES.COLLECTION_DETAIL
    ]
  },
  {
    name: 'description',
    label: 'Description',
    allowedTypes: [
      MODULE_TYPES.COLLECTION_GRID,
      MODULE_TYPES.COLLECTION_LIST,
      MODULE_TYPES.COLLECTION_CAROUSEL,
      MODULE_TYPES.FEATURED_COLLECTION,
      MODULE_TYPES.COLLECTION_DETAIL
    ]
  },
  {
    name: 'viewAll',
    label: 'View All Button',
    allowedTypes: [
      MODULE_TYPES.COLLECTION_GRID,
      MODULE_TYPES.COLLECTION_LIST,
      MODULE_TYPES.COLLECTION_CAROUSEL,
      MODULE_TYPES.COLLECTION_PILL_CAROUSEL
    ]
  },
  {
    name: 'collectionTitle',
    label: 'Collection Title',
    allowedTypes: [MODULE_TYPES.FEATURED_COLLECTION_SLIDER]
  },
  {
    name: 'collectionDescription',
    label: 'Collection Description',
    allowedTypes: [
      MODULE_TYPES.FEATURED_COLLECTION_SLIDER,
      MODULE_TYPES.COLLECTION_LIST,
      MODULE_TYPES.FEATURED_COLLECTION,
      MODULE_TYPES.COLLECTION_GRID
    ]
  },
  {
    name: 'collectionImage',
    label: 'Collection Image',
    allowedTypes: [MODULE_TYPES.FEATURED_COLLECTION_SLIDER]
  },
  {
    name: 'autoAdvanceEnabled',
    label: 'Auto-play Slides',
    allowedTypes: [MODULE_TYPES.FEATURED_COLLECTION_SLIDER]
  },
  {
    name: 'numberOfItems',
    label: 'Number Of Items',
    allowedTypes: [
      MODULE_TYPES.COLLECTION_GRID,
      MODULE_TYPES.COLLECTION_LIST,
      MODULE_TYPES.COLLECTION_CAROUSEL,
      MODULE_TYPES.FEATURED_COLLECTION
    ]
  },
  {
    name: 'separator',
    label: 'Separator',
    allowedTypes: [MODULE_TYPES.COLLECTION_LIST]
  },
  {
    name: 'showImageThumbnail',
    label: 'show Image Thubnail',
    allowedTypes: [MODULE_TYPES.COLLECTION_DETAIL]
  },
  {
    name: 'showProgressBar',
    label: 'show Progress Bar',
    allowedTypes: [MODULE_TYPES.COLLECTION_DETAIL]
  },
  {
    name: 'navigationArrow',
    label: 'Show Navigation Arrow',
    allowedTypes: [MODULE_TYPES.FEATURED_COLLECTION_SLIDER]
  }
];

const AUTO_GENERATED_SORT_BY_OPTIONS = [
  { label: 'Recent', value: AUTO_GENERATED_SORT_BY_TYPES.RECENT },
  { label: 'Random', value: AUTO_GENERATED_SORT_BY_TYPES.RANDOM }
];

const MODULE_KEYS = {
  [MODULE_TYPES.COLLECTION_CAROUSEL]: 'collectionCarouselModule',
  [MODULE_TYPES.COLLECTION_LIST]: 'collectionListModule',
  [MODULE_TYPES.COLLECTION_GRID]: 'collectionGridModule',
  [MODULE_TYPES.FEATURED_COLLECTION]: 'featuredCollectionModule',
  [MODULE_TYPES.FEATURED_COLLECTION_SLIDER]: 'featuredCollectionSliderModule',
  [MODULE_TYPES.COLLECTION_PILL_CAROUSEL]: 'collectionPillCarouselModule',
  [MODULE_TYPES.COLLECTION_DETAIL]: 'collectionDetailModule'
};

const CONFIG_TITLE = {
  [MODULE_TYPES.COLLECTION_CAROUSEL]: 'Collection Carousel Configs',
  [MODULE_TYPES.COLLECTION_LIST]: 'Collection List Configs',
  [MODULE_TYPES.COLLECTION_GRID]: 'Collection Grid Configs',
  [MODULE_TYPES.FEATURED_COLLECTION]: 'Feature Collection Configs',
  [MODULE_TYPES.FEATURED_COLLECTION_SLIDER]:
    'Feature Collection Slider Configs',
  [MODULE_TYPES.COLLECTION_PILL_CAROUSEL]: 'Collection Pill Carousel Configs',
  [MODULE_TYPES.COLLECTION_DETAIL]: 'Collection Detail Configs'
};

const { COLLECTION } = PAGE_TYPES;
const ALLOWED_TEMPLATE_CONFIGS = {
  [MODULE_TYPES.FEATURED_COLLECTION]: [COLLECTION],
  [MODULE_TYPES.COLLECTION_DETAIL]: [COLLECTION]
};

const CollectionForm = ({
  form: { type: formType, moduleId, defaultValues, index: order },
  pageId,
  type,
  onCancel,
  onSuccess,
  isDefaultPage,
  pageType
}) => {
  const [form] = Form.useForm();
  const { data: configData } = useStaticData(STATIC_DATA_KEYS.CONFIGS);
  const isEdit = formType === FORM_TYPES.EDIT;
  const initialValues = useMemo(() => getInitialValues(type), [type]);
  const isMultiCollection =
    type !== MODULE_TYPES.FEATURED_COLLECTION &&
    type !== MODULE_TYPES.COLLECTION_DETAIL;
  const allowedTemplateConfig =
    isDefaultPage && ALLOWED_TEMPLATE_CONFIGS[type]?.includes(pageType);

  const configProps = Form.useWatch(['config'], form);
  const settingsProps = Form.useWatch(['settings'], form);
  const isDefaultModule = Form.useWatch(['isDefaultModule'], form);
  const { autoGenerate } = configProps ?? initialValues.config;

  const autoAdvanceEnabled =
    settingsProps?.autoAdvanceEnabled ??
    initialValues.settings?.autoAdvanceEnabled ??
    false;

  const [addEditModule, { loading }] = useMutation(
    isEdit ? UPDATE_PAGE_MODULE : CREATE_PAGE_MODULE
  );

  useEffect(() => {
    if (moduleId && defaultValues && formType === FORM_TYPES.EDIT && form) {
      const idm = defaultValues?.isDefaultModule;
      form.setFieldsValue({
        title: defaultValues?.title ?? '',
        description: defaultValues?.description ?? '',
        status: defaultValues?.status ?? STATUS_TYPES.DRAFT,
        isDefaultModule: idm,
        permissions:
          defaultValues?.permissions?.map((value) => ({
            label: value,
            value
          })) ?? [],
        settings: {
          ...initialValues.settings,
          ...defaultValues?.moduleData?.settings,
          ...(type === MODULE_TYPES.FEATURED_COLLECTION_SLIDER && {
            autoAdvanceEnabled:
              defaultValues?.moduleData?.settings?.autoAdvanceEnabled ?? false
          })
        },
        config: {
          autoGenerate:
            defaultValues?.moduleData?.config?.autoGenerate ?? false,
          ...(type !== MODULE_TYPES.COLLECTION_PILL_CAROUSEL && {
            aspectRatio:
              RATIOS[defaultValues?.moduleData?.config?.aspectRatio] ??
              RATIOS?.SMALL
          }),
          autoGenerateSortBy:
            defaultValues?.moduleData?.config?.collectionAutoGenerateSortBy ||
            null,
          autoGenerateLimit:
            defaultValues?.moduleData?.config?.autoGenerateLimit || null,
          ...(type === MODULE_TYPES.FEATURED_COLLECTION_SLIDER && {
            autoAdvanceInterval:
              defaultValues?.moduleData?.config?.autoAdvanceInterval ??
              AUTO_ADVANCE_DEFAULTS.INTERVAL,
            autoAdvanceResumeDelay:
              defaultValues?.moduleData?.config?.autoAdvanceResumeDelay ??
              AUTO_ADVANCE_DEFAULTS.RESUME_DELAY
          }),
          collections: isMultiCollection
            ? defaultValues?.moduleData?.config?.collections?.map(
                ({ id, title, thumbnail }) => ({
                  id,
                  title,
                  url: thumbnail?.url ?? ''
                })
              ) || []
            : {
                id: defaultValues?.moduleData?.config?.collection?.id ?? '',
                title:
                  defaultValues?.moduleData?.config?.collection?.title ?? '',
                url:
                  defaultValues?.moduleData?.config?.collection?.thumbnail
                    ?.url ?? ''
              },
          ...([
            MODULE_TYPES.COLLECTION_PILL_CAROUSEL,
            MODULE_TYPES.COLLECTION_CAROUSEL
          ]?.includes(type) && {
            redirectConfig: {
              internalPageType:
                defaultValues?.moduleData?.config?.redirectConfig
                  ?.internalPageType ?? null,
              slug: defaultValues?.moduleData?.config?.redirectConfig?.url
                ? {
                    label: `/${defaultValues.moduleData.config.redirectConfig.url}`,
                    value: defaultValues.moduleData.config.redirectConfig.url
                  }
                : null,
              url:
                defaultValues?.moduleData?.config?.redirectConfig?.url ?? null
            }
          })
        }
      });
    }
  }, [
    form,
    moduleId,
    formType,
    defaultValues,
    form,
    initialValues,
    isMultiCollection
  ]);

  const handleSubmit = ({
    config,
    settings,
    permissions,
    // eslint-disable-next-line no-shadow
    isDefaultModule,
    ...rest
  }) => {
    const key = MODULE_KEYS[type];
    if (!key) return;
    const isDefault = allowedTemplateConfig && isDefaultModule;

    const {
      collections,
      autoGenerate: configAutoGenerate,
      autoGenerateLimit,
      autoAdvanceInterval,
      autoAdvanceResumeDelay,
      redirectConfig,
      ...restConfig
    } = config ?? {};

    const payload = {
      ...(!isEdit && {
        type,
        order: order + 1
      }),
      permissions: permissions?.map(({ value }) => value),
      ...rest,
      [key]: {
        settings,
        config: {
          autoGenerate: configAutoGenerate,
          ...restConfig,
          ...(isDefault
            ? { collectionId: '*' }
            : {
                ...(!configAutoGenerate &&
                  isMultiCollection && {
                    collections:
                      collections.map(({ id }, i) => ({
                        collectionId: id,
                        order: i + 1
                      })) ?? []
                  }),
                ...(!configAutoGenerate &&
                  !isMultiCollection && {
                    collectionId: collections?.id ?? ''
                  })
              }),
          ...(autoGenerateLimit && {
            autoGenerateLimit: Number(autoGenerateLimit)
          }),
          ...(type === MODULE_TYPES.FEATURED_COLLECTION_SLIDER && {
            ...(autoAdvanceInterval && {
              autoAdvanceInterval: Number(autoAdvanceInterval)
            }),
            ...(autoAdvanceResumeDelay && {
              autoAdvanceResumeDelay: Number(autoAdvanceResumeDelay)
            })
          }),
          ...([
            MODULE_TYPES.COLLECTION_PILL_CAROUSEL,
            MODULE_TYPES.COLLECTION_CAROUSEL
          ]?.includes(type) &&
            redirectConfig?.url && {
              redirectConfig: {
                internalPageType: redirectConfig?.internalPageType ?? null,
                url: redirectConfig?.url ?? null
              }
            })
        }
      }
    };

    addEditModule({
      variables: { data: payload, id: isEdit ? moduleId : pageId }
    }).then(
      ({
        data: {
          addUpdatedPageModule: { pageModule }
        }
      }) => {
        onSuccess(pageModule);
      }
    );
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <ModuleFields />
      <Space className="w-full" direction="vertical">
        <ShowFields settings={SETTINGS} type={type} />
        <Space className="w-full" direction="vertical">
          {allowedTemplateConfig && pageType === COLLECTION && (
            <>
              <Typography.Text>Template Config</Typography.Text>
              <Form.Item
                className="m-0"
                name="isDefaultModule"
                valuePropName="checked"
              >
                <Switch label="Use Template Data" />
              </Form.Item>
            </>
          )}
          {(!allowedTemplateConfig ||
            (allowedTemplateConfig && !isDefaultModule)) && (
            <>
              <Typography.Text>{CONFIG_TITLE[type]}</Typography.Text>
              <div>
                {type !== MODULE_TYPES.COLLECTION_DETAIL && (
                  <Form.Item
                    name={['config', 'autoGenerate']}
                    valuePropName="checked"
                  >
                    <Switch
                      label={
                        isMultiCollection
                          ? 'Auto Generated Collections'
                          : 'Auto Generated Collection'
                      }
                    />
                  </Form.Item>
                )}
                {type !== MODULE_TYPES.COLLECTION_PILL_CAROUSEL &&
                  type !== MODULE_TYPES.COLLECTION_DETAIL && (
                    <Form.Item
                      label="Aspect Ratio"
                      name={['config', 'aspectRatio']}
                    >
                      <AntdSelect
                        options={RATIOS_OPTIONS}
                        placeholder="Please select aspect ratio"
                      />
                    </Form.Item>
                  )}
                {type === MODULE_TYPES.FEATURED_COLLECTION_SLIDER &&
                  autoAdvanceEnabled && (
                    <>
                      <Form.Item
                        label="Time Between Slides (seconds)"
                        name={['config', 'autoAdvanceInterval']}
                        rules={[
                          formValidatorRules?.number,
                          formValidatorRules?.minNumberAllowed(
                            AUTO_ADVANCE_DEFAULTS.MIN_INTERVAL
                          ),
                          formValidatorRules?.maxNumberAllowed(
                            AUTO_ADVANCE_DEFAULTS.MAX_INTERVAL,
                            `Time must be between ${AUTO_ADVANCE_DEFAULTS.MIN_INTERVAL} and ${AUTO_ADVANCE_DEFAULTS.MAX_INTERVAL} seconds`
                          )
                        ]}
                        extra={`Default: ${AUTO_ADVANCE_DEFAULTS.INTERVAL} seconds. Range: ${AUTO_ADVANCE_DEFAULTS.MIN_INTERVAL}-${AUTO_ADVANCE_DEFAULTS.MAX_INTERVAL} seconds.`}
                      >
                        <Input
                          type="number"
                          placeholder="Enter time in seconds"
                          min={AUTO_ADVANCE_DEFAULTS.MIN_INTERVAL}
                          max={AUTO_ADVANCE_DEFAULTS.MAX_INTERVAL}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Resume Delay (seconds)"
                        name={['config', 'autoAdvanceResumeDelay']}
                        rules={[
                          formValidatorRules?.number,
                          formValidatorRules?.minNumberAllowed(
                            AUTO_ADVANCE_DEFAULTS.MIN_RESUME_DELAY
                          ),
                          formValidatorRules?.maxNumberAllowed(
                            AUTO_ADVANCE_DEFAULTS.MAX_RESUME_DELAY,
                            `Delay must be between ${AUTO_ADVANCE_DEFAULTS.MIN_RESUME_DELAY} and ${AUTO_ADVANCE_DEFAULTS.MAX_RESUME_DELAY} seconds`
                          )
                        ]}
                        extra={`Default: ${AUTO_ADVANCE_DEFAULTS.RESUME_DELAY} seconds. Range: ${AUTO_ADVANCE_DEFAULTS.MIN_RESUME_DELAY}-${AUTO_ADVANCE_DEFAULTS.MAX_RESUME_DELAY} seconds.`}
                      >
                        <Input
                          type="number"
                          placeholder="Enter delay in seconds"
                          min={AUTO_ADVANCE_DEFAULTS.MIN_RESUME_DELAY}
                          max={AUTO_ADVANCE_DEFAULTS.MAX_RESUME_DELAY}
                        />
                      </Form.Item>
                    </>
                  )}
                {autoGenerate ? (
                  <>
                    <Form.Item
                      label="Auto Generated Sort By"
                      name={['config', 'autoGenerateSortBy']}
                    >
                      <AntdSelect
                        options={AUTO_GENERATED_SORT_BY_OPTIONS}
                        placeholder="Select sort by"
                      />
                    </Form.Item>
                    {isMultiCollection && (
                      <Form.Item
                        label="No. of Auto generated Collections"
                        name={['config', 'autoGenerateLimit']}
                        rules={[
                          formValidatorRules?.number,
                          formValidatorRules?.maxNumberAllowed(
                            configData?.MAX_AUTO_GENERATE_LIMIT?.value || 20
                          )
                        ]}
                      >
                        <Input placeholder="Enter number" />
                      </Form.Item>
                    )}
                  </>
                ) : (
                  <Form.Item
                    label={
                      isMultiCollection
                        ? 'Select Collections'
                        : 'Select Collection'
                    }
                    name={['config', 'collections']}
                    extra={
                      isMultiCollection ? 'Select items in order you want' : ''
                    }
                  >
                    <SelectCollections multiple={isMultiCollection} />
                  </Form.Item>
                )}
                {[
                  MODULE_TYPES.COLLECTION_PILL_CAROUSEL,
                  MODULE_TYPES.COLLECTION_CAROUSEL
                ]?.includes(type) && (
                  <RedirectConfigInput
                    namePath={['config', 'redirectConfig']}
                    form={form}
                  />
                )}
              </div>
            </>
          )}
        </Space>
        <Permissions />
        <div className="d-flex button-section">
          <Space>
            <Form.Item>
              <Button
                type="text"
                htmlType="submit"
                className="text-btn mr-8"
                size="middle"
                disabled={loading}
                loading={loading}
              >
                Save
              </Button>
            </Form.Item>

            <Form.Item>
              <Button
                disabled={loading}
                onClick={onCancel}
                type="text"
                className="text-btn2"
              >
                Cancel
              </Button>
            </Form.Item>
          </Space>
        </div>
      </Space>
    </Form>
  );
};

export default CollectionForm;
